// 逐頁 SEO：零依賴（不需 react-helmet），用 useEffect 直接維護 <head> 標籤。
// 站台預設從後台 /api/settings/seo 讀（後台可改）；逐頁預設寫在 PAGE_META（後台未設時的底）。
// 商品頁 title/description/JSON-LD 由 ProductDetail 帶商品資料自組；FAQ 由 Faq 頁組 FAQPage。
// 注意：純前端 SPA，Google 會執行 JS 讀得到；不跑 JS 的 AI 爬蟲 / 分享卡片靠後端 SSR-lite 注入（public_main.py）。
import React, { createContext, useContext, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

const API_BASE = process.env.REACT_APP_API_BASE || '';

// 品牌統一寫法：中文品牌 + 英文 + 品類，幫助 Google/AI 和其他同名 ShiningStars 拆開。
const SITE = '星河耀眼 ShiningStar Jewelry';

const FALLBACK = {
  origin: 'https://shiningstars.tw',
  site_title: SITE,
  title_template: '{page}｜' + SITE,
  default_title: SITE + '｜客製水晶手鍊・天然石飾品',
  meta_description:
    '星河耀眼 ShiningStar Jewelry 是客製水晶手鍊品牌，依五行、生命靈數、願望與色調，為你手作專屬天然石手鍊。',
  keywords: '客製水晶手鍊,天然石手鍊,水晶手鍊,能量手鍊,五行水晶,生命靈數,療癒水晶',
  og_title: SITE,
  og_description: '結合命理分析與水晶能量，依五行、生命靈數與願望，為此刻的你設計專屬水晶手鍊。',
  og_image: 'https://shiningstars.tw/logo.png',
  // 社群/外部連結：供首頁 Organization sameAs（後台可覆蓋）。留空則不輸出。
  sameas: [],
  pages: {},
};

// 逐頁預設 meta（後台 seo_pages 未覆蓋時使用）。每頁 title/description 皆唯一，避免全站雷同。
const PAGE_META = {
  '/': {
    home: true,
    title: SITE + '｜客製水晶手鍊・天然石飾品',
    description:
      '星河耀眼 ShiningStar Jewelry 是客製水晶手鍊品牌。依五行、生命靈數、願望與色調，為你手作專屬天然石手鍊——不只是飾品，是剛剛好的能量陪伴。',
  },
  '/shop': {
    title: '水晶手鍊商城｜天然石・客製能量手鍊｜' + SITE,
    description:
      '瀏覽星河耀眼的天然水晶手鍊與現貨作品：粉晶、紫水晶、黃水晶等多種天然石，並支援依五行與生命靈數客製專屬手鍊。',
  },
  '/custom': {
    title: '客製水晶手鍊｜依五行、生命靈數與願望設計｜星河耀眼',
    description:
      '填入生日與願望，我們依五行平衡與生命靈數為你配石排列，設計專屬水晶手鍊。3～7 個工作天手作完成、從台灣出貨。',
  },
  '/about': {
    title: '關於星河耀眼｜客製天然水晶手鍊品牌',
    description:
      '星河耀眼 ShiningStar Jewelry 是客製水晶手鍊品牌，結合命理與天然石能量，為每個人手作剛剛好的專屬手鍊。',
  },
  '/faq': {
    title: '客製水晶手鍊常見問題｜流程、尺寸、付款、修改與保養',
    description:
      '客製水晶手鍊怎麼下單？手圍怎麼量？可以改幾次？付款、出貨、退換與保養消磁——星河耀眼常見問題一次看懂。',
  },
  '/crystal-guide': {
    title: '水晶入門指南｜新手如何挑選適合自己的水晶手鍊｜星河耀眼',
    description:
      '第一次買水晶手鍊怎麼選？從功效、顏色、預算到五行與生命靈數，帶你避開常見誤區，挑到剛剛好的那一條。',
  },
  // 交易/會員頁：robots.txt 已 Disallow，這裡仍給明確標題，避免萬一被索引時語意混亂。
  '/cart': { title: '購物車｜' + SITE, description: '確認你的水晶手鍊訂單內容。' },
  '/wishlist': { title: '願望清單｜' + SITE, description: '你收藏的水晶手鍊作品。' },
  '/member': { title: '會員中心｜' + SITE, description: '星河耀眼會員中心。' },
  '/orders': { title: '訂單查詢｜' + SITE, description: '查詢你的水晶手鍊訂單狀態。' },
};

const SeoCtx = createContext(FALLBACK);
export const useSeo = () => useContext(SeoCtx) || FALLBACK;

export function SeoProvider({ children }) {
  const [cfg, setCfg] = useState(FALLBACK);
  useEffect(() => {
    fetch(`${API_BASE}/api/settings/seo`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (d) setCfg({ ...FALLBACK, ...d, pages: d.pages || {} }); })
      .catch(() => { /* 保持預設 */ });
  }, []);
  return <SeoCtx.Provider value={cfg}>{children}</SeoCtx.Provider>;
}

function upsert(selector, create, attrs) {
  let el = document.head.querySelector(selector);
  if (!el) { el = create(); document.head.appendChild(el); }
  Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, String(v)));
}
function setMeta(name, isProp, content) {
  if (content == null || content === '') return;
  const attr = isProp ? 'property' : 'name';
  upsert(`meta[${attr}="${name}"]`, () => {
    const m = document.createElement('meta');
    m.setAttribute(attr, name);
    return m;
  }, { content });
}

// JSON-LD 結構化資料：以 id 管理 <script type="application/ld+json">，切頁時自動清掉。
// data 傳單一物件或陣列皆可；傳 null 表示移除。
export function JsonLd({ id, data }) {
  useEffect(() => {
    const sid = `ld-${id}`;
    const existing = document.getElementById(sid);
    if (data == null) { if (existing) existing.remove(); return; }
    const el = existing || document.createElement('script');
    if (!existing) { el.type = 'application/ld+json'; el.id = sid; document.head.appendChild(el); }
    try { el.textContent = JSON.stringify(data); } catch { /* ignore */ }
    return () => { const e = document.getElementById(sid); if (e) e.remove(); };
  }, [id, data]);
  return null;
}

// page: 頁面短標題（會套 title_template）；title: 完整標題（優先，直接使用不套樣板）。
export function Seo({ page, title, description, image, path, type = 'website' }) {
  const cfg = useSeo();
  useEffect(() => {
    const fullTitle = title
      || (page ? cfg.title_template.replace('{page}', page) : cfg.default_title);
    const desc = description || cfg.meta_description;
    const img = image || cfg.og_image;
    const url = `${cfg.origin}${path || window.location.pathname}`;
    document.title = fullTitle;
    setMeta('description', false, desc);
    setMeta('keywords', false, cfg.keywords);
    upsert('link[rel="canonical"]', () => {
      const l = document.createElement('link'); l.setAttribute('rel', 'canonical'); return l;
    }, { href: url });
    setMeta('og:site_name', true, cfg.site_title);
    setMeta('og:title', true, fullTitle);
    setMeta('og:description', true, desc);
    setMeta('og:image', true, img);
    setMeta('og:url', true, url);
    setMeta('og:type', true, type);
    setMeta('twitter:card', false, 'summary_large_image');
    setMeta('twitter:title', false, fullTitle);
    setMeta('twitter:description', false, desc);
    setMeta('twitter:image', false, img);
  }, [cfg, page, title, description, image, path, type]);
  return null;
}

// 首頁 Organization + WebSite JSON-LD：讓 Google/AI 知道「星河耀眼」是水晶飾品品牌，
// 消除與其他 ShiningStars（海外團體 / 玩具品牌等）的同名混淆。
function homeJsonLd(cfg) {
  const org = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: SITE,
    alternateName: ['星河耀眼', 'ShiningStar Jewelry', 'ShiningStars'],
    url: cfg.origin,
    logo: `${cfg.origin}/logo.png`,
    description:
      '天然水晶與客製手鍊品牌，依五行、生命靈數、願望與色調設計專屬水晶手鍊。',
  };
  if (Array.isArray(cfg.sameas) && cfg.sameas.length) org.sameAs = cfg.sameas;
  const website = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: SITE,
    url: cfg.origin,
    inLanguage: 'zh-Hant-TW',
  };
  return [org, website];
}

// 掛在 Router 內：依路由自動設定靜態頁 SEO；商品頁交給 ProductDetail 自組。
export function RouteSeo() {
  const loc = useLocation();
  const cfg = useSeo();
  const isHome = loc.pathname === '/';
  // 商品頁、知識文章詳情頁各自帶資料自組 SEO
  if (loc.pathname.startsWith('/product/')) return null;
  if (loc.pathname.startsWith('/crystal-guide/')) return null;

  const meta = PAGE_META[loc.pathname] || null;
  const override = (cfg.pages && cfg.pages[loc.pathname]) || null;
  const title = (override && override.title) || (meta && meta.title) || undefined;
  const description = (override && override.description) || (meta && meta.description) || undefined;

  return (
    <>
      <Seo title={title} description={description} path={loc.pathname} />
      {isHome && <JsonLd id="home" data={homeJsonLd(cfg)} />}
    </>
  );
}
